import React, { useEffect, useMemo, useState } from 'react';
import { io } from 'socket.io-client';
import { formatDeviceTimestamp, isDeviceOnline } from '../utils/timeUtils';
import { authFetch } from '../utils/authFetch';
import { emitJoinFarmRooms, subscribeSocketFarmRooms } from '../utils/farmSocketRooms';
import { getSocketIoServerUrl, SOCKET_IO_CLIENT_OPTIONS } from '../utils/socketIoClientOptions';
import {
  EDITABLE_WATER_THRESHOLD_KEYS,
  buildWaterThresholdState,
  serializeWaterThreshold,
} from '../utils/waterThresholds';
import WaterCharts from './WaterCharts';
import DeviceSettingsModal from './DeviceSettingsModal';
import './DeviceCard.css';

let sharedWaterSocket = null;
const WATER_LIVE_POLL_INTERVAL_MS = Number(process.env.REACT_APP_WATER_LIVE_POLL_MS) || 30_000;

function particleDeviceIdsEqual(a, b) {
  const s = String(a || '').trim();
  const t = String(b || '').trim();
  if (!s || !t) return false;
  if (s === t) return true;
  if (/^[a-f0-9]{24}$/i.test(s) && /^[a-f0-9]{24}$/i.test(t)) return s.toLowerCase() === t.toLowerCase();
  return false;
}

function getSharedWaterSocket() {
  if (!sharedWaterSocket) {
    sharedWaterSocket = io(getSocketIoServerUrl(), SOCKET_IO_CLIENT_OPTIONS);
    subscribeSocketFarmRooms(sharedWaterSocket);
  }
  return sharedWaterSocket;
}

function formatNumber(value, digits = 2) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  return Number(value).toFixed(digits);
}

function MetricTile({ icon, label, value, unit, tone = '', hint = '', prominent = false, hasError = false }) {
  return (
    <div className={`water-status-card__metric h-100 ${prominent ? 'water-status-card__metric--primary' : ''}`}>
      <div className="water-status-card__metric-icon" aria-hidden="true">
        <i className={icon} />
      </div>
      <div>
        <div className="text-muted small text-uppercase d-flex align-items-center gap-1">
          <span>{label}</span>
          {hasError ? <i className="fas fa-triangle-exclamation water-status-card__error-icon" aria-label={`${label} error`} /> : null}
        </div>
        <div className={`water-status-card__metric-value ${tone}`}>
          {value}
          {unit ? <span className="water-status-card__metric-unit"> {unit}</span> : null}
        </div>
        {hint ? <div className="small text-muted">{hint}</div> : null}
      </div>
    </div>
  );
}

function formatCompactNumber(value, digits = 0) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
  return Number(value).toFixed(digits);
}

function getTankFillColor(percent) {
  const blue = { r: 52, g: 152, b: 219 };
  const red = { r: 231, g: 76, b: 60 };

  if (percent >= 15) {
    return `rgb(${blue.r}, ${blue.g}, ${blue.b})`;
  }

  const ratio = Math.max(0, Math.min(1, (15 - percent) / 15));
  const r = Math.round(blue.r + (red.r - blue.r) * ratio);
  const g = Math.round(blue.g + (red.g - blue.g) * ratio);
  const b = Math.round(blue.b + (red.b - blue.b) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function asPlainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function getWaterPayloadDeviceId(payload = {}) {
  return String(payload?.deviceId || payload?.device_id || payload?.id || '').trim();
}

function telemetryTimeMs(source = {}) {
  const ts =
    source.lastRealSensorUpdateAt ||
    source.latestTelemetryAt ||
    source.lastTelemetryAt ||
    source.latestTelemetry?.timestamp ||
    source.timestamp ||
    source.ts ||
    null;
  const ms = ts ? new Date(ts).getTime() : 0;
  return Number.isFinite(ms) ? ms : 0;
}

function waterSocketDebug(...args) {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('waterSocketDebug') === '1') {
      console.debug('[water:socket]', ...args);
    }
  } catch (_) {
    /* debug only */
  }
}

function mergeWaterLivePayload(prev = {}, payload = {}, options = {}) {
  const previous = asPlainObject(prev);
  const incoming = asPlainObject(payload);
  const incomingTelemetry = asPlainObject(incoming.latestTelemetry);
  const data = asPlainObject(incoming.data);
  const source = {
    ...data,
    ...incoming,
    ...incomingTelemetry,
  };
  const telemetryTimestamp =
    incoming.lastRealSensorUpdateAt ||
    incoming.latestTelemetryAt ||
    incoming.lastTelemetryAt ||
    incomingTelemetry.timestamp ||
    incoming.timestamp ||
    incoming.ts ||
    new Date().toISOString();
  const incomingMs = telemetryTimestamp ? new Date(telemetryTimestamp).getTime() : 0;
  const prevMs = telemetryTimeMs(previous);
  if (Number.isFinite(incomingMs) && incomingMs > 0 && prevMs > 0 && incomingMs < prevMs) {
    waterSocketDebug('stale payload ignored', {
      source: options.source || 'unknown',
      deviceId: getWaterPayloadDeviceId(incoming),
      incoming: telemetryTimestamp,
      previous: previous.lastRealSensorUpdateAt || previous.latestTelemetryAt || previous.lastTelemetryAt || previous.timestamp,
    });
    return previous;
  }
  const telemetryReason = firstDefined(
    source.telemetry_reason,
    source.telemetryReason,
    previous.latestTelemetry?.telemetry_reason,
    previous.telemetry_reason
  );

  return {
    ...previous,
    status: incoming.status || previous.status || 'online',
    isOnline: String(incoming.status || previous.status || 'online').toLowerCase() !== 'offline',
    timestamp: telemetryTimestamp,
    lastUpdated: telemetryTimestamp,
    lastTelemetryAt: telemetryTimestamp,
    latestTelemetryAt: telemetryTimestamp,
    lastRealSensorUpdateAt: telemetryTimestamp,
    latestTelemetry: {
      ...asPlainObject(previous.latestTelemetry),
      ...incomingTelemetry,
      timestamp: incomingTelemetry.timestamp || telemetryTimestamp,
      ph: firstDefined(source.ph, source.pH, source.PH, previous.latestTelemetry?.ph),
      ec: firstDefined(source.ec, source.EC, source.ec_mscm, previous.latestTelemetry?.ec),
      orp: firstDefined(source.orp, source.ORP, source.orp_mv, previous.latestTelemetry?.orp),
      temp: firstDefined(source.temp, source.ecTemp, source.temperature, source.temp_c, previous.latestTelemetry?.temp),
      temperature: firstDefined(source.temperature, source.temp, source.ecTemp, source.temp_c, previous.latestTelemetry?.temperature),
      ecTemp: firstDefined(source.ecTemp, source.temp, source.temperature, source.temp_c, previous.latestTelemetry?.ecTemp),
      level: firstDefined(source.level, source.waterLevel, source.water_level, previous.latestTelemetry?.level),
      levelCm: firstDefined(source.levelCm, source.level_cm, previous.latestTelemetry?.levelCm),
      volume: firstDefined(source.volume, previous.latestTelemetry?.volume),
      levelVoltage: firstDefined(source.levelVoltage, source.level_voltage, previous.latestTelemetry?.levelVoltage),
      phError: firstDefined(source.phError, previous.latestTelemetry?.phError),
      ecError: firstDefined(source.ecError, previous.latestTelemetry?.ecError),
      orpError: firstDefined(source.orpError, previous.latestTelemetry?.orpError),
      tankMaxHeight: firstDefined(source.tankMaxHeight, previous.latestTelemetry?.tankMaxHeight),
      tankFullVolume: firstDefined(source.tankFullVolume, previous.latestTelemetry?.tankFullVolume),
      transport: firstDefined(source.transport, previous.latestTelemetry?.transport),
      telemetry_reason: telemetryReason,
      telemetryReason: telemetryReason,
    },
    data: {
      ...asPlainObject(previous.data),
      ...data,
    },
    deviceStatus: {
      ...asPlainObject(previous.deviceStatus),
      ...asPlainObject(incoming.deviceStatus),
      status: incoming.deviceStatus?.status || incoming.status || previous.deviceStatus?.status || 'online',
      lastSeenAt: incoming.deviceStatus?.lastSeenAt || incoming.timestamp || telemetryTimestamp,
    },
    ph: firstDefined(source.ph, source.pH, source.PH, previous.ph),
    ec: firstDefined(source.ec, source.EC, source.ec_mscm, previous.ec),
    orp: firstDefined(source.orp, source.ORP, source.orp_mv, previous.orp),
    temp: firstDefined(source.temp, source.ecTemp, source.temperature, source.temp_c, previous.temp),
    temperature: firstDefined(source.temperature, source.temp, source.ecTemp, source.temp_c, previous.temperature),
    ecTemp: firstDefined(source.ecTemp, source.temp, source.temperature, source.temp_c, previous.ecTemp),
    level: firstDefined(source.level, source.waterLevel, source.water_level, previous.level),
    levelCm: firstDefined(source.levelCm, source.level_cm, previous.levelCm),
    volume: firstDefined(source.volume, previous.volume),
    levelVoltage: firstDefined(source.levelVoltage, source.level_voltage, previous.levelVoltage),
    phError: firstDefined(source.phError, previous.phError),
    ecError: firstDefined(source.ecError, previous.ecError),
    orpError: firstDefined(source.orpError, previous.orpError),
    tankMaxHeight: firstDefined(source.tankMaxHeight, previous.tankMaxHeight),
    tankFullVolume: firstDefined(source.tankFullVolume, previous.tankFullVolume),
    transport: firstDefined(source.transport, previous.transport),
    telemetry_reason: telemetryReason,
    telemetryReason,
  };
}

function toBooleanFlag(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const token = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'error'].includes(token)) return true;
    if (['false', '0', 'no', 'ok'].includes(token)) return false;
  }
  return Boolean(value);
}

function toDraftValue(value) {
  const resolved = firstDefined(value);
  return resolved === undefined ? '' : String(resolved);
}

function buildWaterConfigDraft(config = {}, device = {}) {
  const safeConfig = asPlainObject(config);
  const safeDevice = asPlainObject(device);
  const tank = asPlainObject(safeConfig.tank);
  const intervals = asPlainObject(safeConfig.intervals);
  return {
    tankMaxHeight: toDraftValue(firstDefined(safeConfig.tankMaxHeight, tank.maxHeight, safeDevice.tankMaxHeight)),
    tankFullVolume: toDraftValue(firstDefined(safeConfig.tankFullVolume, tank.fullVolume, safeDevice.tankFullVolume)),
    dMin: toDraftValue(firstDefined(safeConfig.displayInterval, safeConfig.dMin, safeConfig.display_min, intervals.display_min)),
    sMin: toDraftValue(firstDefined(safeConfig.serverInterval, safeConfig.sMin, safeConfig.server_min, intervals.server_min)),
  };
}

function serializeWaterConfig(draft = {}) {
  const payload = {};
  for (const [key, value] of Object.entries(draft)) {
    const text = String(value ?? '').trim();
    if (!text) continue;
    const numeric = Number(text);
    if (Number.isFinite(numeric)) payload[key] = numeric;
  }
  return payload;
}

export default function WaterStatusCard({ device = {}, lang, t, onRefresh, roomId = '' }) {
  const safeDevice = asPlainObject(device);
  const [expanded, setExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= 768;
  });
  const [showSettings, setShowSettings] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [nameError, setNameError] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [pendingFreshData, setPendingFreshData] = useState(false);
  const [msg, setMsg] = useState(null);
  const [liveConfigError, setLiveConfigError] = useState('');
  const [telegramEnabled, setTelegramEnabled] = useState(safeDevice.telegramEnabled ?? safeDevice.alertsEnabled !== false);
  const [trendAlerts, setTrendAlerts] = useState(safeDevice.trendAlerts ?? safeDevice.trendAlertsEnabled ?? false);
  const [powerAlerts, setPowerAlerts] = useState(safeDevice.powerAlerts ?? safeDevice.notifyOnOffline ?? true);
  const [thresholdAlerts, setThresholdAlerts] = useState(safeDevice.thresholdAlerts ?? safeDevice.alertsEnabled !== false);
  const [config, setConfig] = useState({});
  const [waterConfigDraft, setWaterConfigDraft] = useState(() => buildWaterConfigDraft(safeDevice.config || {}, safeDevice));
  const [initialWaterConfigDraft, setInitialWaterConfigDraft] = useState(() => buildWaterConfigDraft(safeDevice.config || {}, safeDevice));
  const [socketOverride, setSocketOverride] = useState(null);
  const [thresholds, setThresholds] = useState(() => buildWaterThresholdState(safeDevice.thresholds || {}, safeDevice.config || {}));
  const [initialThresholds, setInitialThresholds] = useState(() => buildWaterThresholdState(safeDevice.thresholds || {}, safeDevice.config || {}));

  const toDisplay = (value) => (value ? value.replace(/_/g, ' ') : value);
  const toSend = (value) => (value ? value.replace(/\s+/g, '_') : value);
  const [name, setName] = useState(toDisplay(safeDevice.customName || safeDevice.name));
  const displayDevice = useMemo(() => {
    if (roomId) return safeDevice;
    if (!socketOverride) return safeDevice;
    const deviceTelemetryMs = telemetryTimeMs(safeDevice);
    const socketTelemetryMs = telemetryTimeMs(socketOverride);
    const latestTelemetry =
      deviceTelemetryMs > socketTelemetryMs
        ? safeDevice.latestTelemetry || socketOverride.latestTelemetry || null
        : socketOverride.latestTelemetry || safeDevice.latestTelemetry || null;
    return {
      ...safeDevice,
      ...socketOverride,
      latestTelemetry,
      lastTelemetryAt:
        deviceTelemetryMs > socketTelemetryMs
          ? safeDevice.lastTelemetryAt || safeDevice.latestTelemetryAt || latestTelemetry?.timestamp || socketOverride.lastTelemetryAt || null
          : socketOverride.lastTelemetryAt || socketOverride.latestTelemetryAt || latestTelemetry?.timestamp || safeDevice.lastTelemetryAt || null,
      latestTelemetryAt:
        deviceTelemetryMs > socketTelemetryMs
          ? safeDevice.latestTelemetryAt || safeDevice.lastTelemetryAt || latestTelemetry?.timestamp || socketOverride.latestTelemetryAt || null
          : socketOverride.latestTelemetryAt || socketOverride.lastTelemetryAt || latestTelemetry?.timestamp || safeDevice.latestTelemetryAt || null,
      lastRealSensorUpdateAt:
        deviceTelemetryMs > socketTelemetryMs
          ? safeDevice.lastRealSensorUpdateAt || latestTelemetry?.timestamp || socketOverride.lastRealSensorUpdateAt || null
          : socketOverride.lastRealSensorUpdateAt || latestTelemetry?.timestamp || safeDevice.lastRealSensorUpdateAt || null,
      data: {
        ...asPlainObject(safeDevice.data),
        ...asPlainObject(socketOverride.data),
      },
      deviceStatus: {
        ...asPlainObject(safeDevice.deviceStatus),
        ...asPlainObject(socketOverride.deviceStatus),
      },
      meta: {
        ...asPlainObject(safeDevice.meta),
        ...asPlainObject(socketOverride.meta),
      },
    };
  }, [roomId, safeDevice, socketOverride]);
  const latestTelemetry = displayDevice.latestTelemetry && typeof displayDevice.latestTelemetry === 'object'
    ? displayDevice.latestTelemetry
    : {};
  const timestampSource =
    displayDevice.lastRealSensorUpdateAt ||
    displayDevice.latestTelemetryAt ||
    displayDevice.lastTelemetryAt ||
    latestTelemetry.timestamp ||
    displayDevice.timestamp ||
    displayDevice.ts ||
    null;
  const locale = lang === 'he' ? 'he-IL' : lang === 'es' ? 'es-ES' : lang;
  const particleStatus = String(displayDevice.deviceStatus?.status || displayDevice.status || '').trim().toLowerCase();
  const statusOnline =
    particleStatus === 'online' || particleStatus === 'idle'
      ? true
      : particleStatus === 'offline'
        ? false
        : isDeviceOnline(timestampSource);
  const sensorDataFresh = displayDevice.sensorDataFreshness?.fresh ?? displayDevice.sensorDataFresh ?? isDeviceOnline(timestampSource);
  const timestampLabel = useMemo(
    () => (timestampSource ? formatDeviceTimestamp(timestampSource, locale) : '-'),
    [timestampSource, locale]
  );
  const data = displayDevice.data || {};
  const ph = firstDefined(latestTelemetry.ph, displayDevice.ph, data.ph, data.pH, data.PH, null);
  const ec = firstDefined(latestTelemetry.ec, displayDevice.ec, data.ec, data.EC, data.ec_mscm, null);
  const orp = firstDefined(latestTelemetry.orp, displayDevice.orp, data.orp, data.ORP, data.orp_mv, null);
  const ecTemp = firstDefined(
    latestTelemetry.ecTemp,
    latestTelemetry.temp,
    latestTelemetry.temperature,
    displayDevice.ecTemp,
    displayDevice.temp,
    displayDevice.temperature,
    data.ecTemp,
    data.temp,
    data.temperature,
    data.temp_c,
    null
  );
  const level = firstDefined(
    latestTelemetry.level,
    latestTelemetry.waterLevel,
    displayDevice.level,
    displayDevice.waterLevel,
    data.level,
    data.waterLevel,
    data.water_level,
    null
  );
  const levelCm = firstDefined(
    latestTelemetry.levelCm,
    latestTelemetry.level_cm,
    displayDevice.levelCm,
    displayDevice.level_cm,
    data.levelCm,
    data.level_cm,
    level
  );
  const volume = firstDefined(latestTelemetry.volume, displayDevice.volume, data.volume, null);
  const levelVoltage = firstDefined(
    latestTelemetry.levelVoltage,
    latestTelemetry.level_voltage,
    displayDevice.levelVoltage,
    displayDevice.level_voltage,
    data.levelVoltage,
    data.level_voltage,
    null
  );
  const tankMaxHeight = firstDefined(latestTelemetry.tankMaxHeight, displayDevice.tankMaxHeight, data.tankMaxHeight, null);
  const tankFullVolume = firstDefined(latestTelemetry.tankFullVolume, displayDevice.tankFullVolume, data.tankFullVolume, null);
  const hasLevelCm = levelCm != null && Number.isFinite(Number(levelCm));
  const fillLevel = hasLevelCm ? levelCm : level;
  const hasRealTankData =
    (fillLevel != null && Number(fillLevel) > 0) || (volume != null && Number(volume) > 0);
  const hasCalibration =
    volume != null &&
    tankFullVolume != null &&
    Number(volume) >= 0 &&
    Number(tankFullVolume) > 0;
  const fillPercent = hasCalibration
    ? Math.max(0, Math.min(100, (Number(volume) / Number(tankFullVolume)) * 100))
    : hasRealTankData
      ? Math.max(
          0,
          Math.min(
            100,
            hasLevelCm && tankMaxHeight != null && Number(tankMaxHeight) > 0
              ? (Number(levelCm) / Number(tankMaxHeight)) * 100
              : Number(level ?? 0)
          )
        )
      : 0;
  const fillColor = getTankFillColor(fillPercent);
  const phError = toBooleanFlag(firstDefined(latestTelemetry.phError, displayDevice.phError, data.phError, false));
  const ecError = toBooleanFlag(firstDefined(latestTelemetry.ecError, displayDevice.ecError, data.ecError, false));
  const orpError = toBooleanFlag(firstDefined(latestTelemetry.orpError, displayDevice.orpError, data.orpError, false));
  const inState = displayDevice.inState ?? data.inState ?? data.inletState ?? data['inlet-state'] ?? null;
  const outState =
    displayDevice.outState ??
    displayDevice.outletState ??
    data.outState ??
    data.outletState ??
    data['outlet-state'] ??
    null;

  const phTone =
    ph != null && Number(ph) >= 5.5 && Number(ph) <= 6.5 ? 'text-success' : 'text-warning';
  const tempWarning = ecTemp != null && Number(ecTemp) > 28;
  const deviceLookupId = displayDevice.id || displayDevice.deviceId;
  const title =
    name ||
    toDisplay(displayDevice.deviceName || displayDevice.alias || displayDevice.displayName || displayDevice.device_name) ||
    displayDevice.deviceId ||
    displayDevice.id;
  const subtitle = displayDevice.deviceId || displayDevice.id;

  useEffect(() => {
    if (roomId) {
      setSocketOverride(null);
      return undefined;
    }
    const localId = String(safeDevice.deviceId || safeDevice.device_id || safeDevice.id || '');
    if (!localId) return undefined;
    const socket = getSharedWaterSocket();
    emitJoinFarmRooms(socket);

    const mergeTelemetryPayload = (payload = {}) => {
      const incoming = asPlainObject(payload);
      const incomingId = getWaterPayloadDeviceId(incoming);
      if (!incomingId || !particleDeviceIdsEqual(incomingId, localId)) {
        waterSocketDebug('device_data ignored', { incomingId, localId, payload: incoming });
        return;
      }
      waterSocketDebug('device_data accepted', {
        incomingId,
        localId,
        timestamp:
          incoming.lastRealSensorUpdateAt ||
          incoming.latestTelemetryAt ||
          incoming.lastTelemetryAt ||
          incoming.latestTelemetry?.timestamp ||
          incoming.timestamp ||
          incoming.ts,
      });
      setSocketOverride((prev) => mergeWaterLivePayload(prev, incoming, { source: 'socket' }));
    };

    const handleDeviceStatus = (payload = {}) => {
      const incoming = asPlainObject(payload);
      const incomingId = getWaterPayloadDeviceId(incoming);
      if (!incomingId || !particleDeviceIdsEqual(incomingId, localId)) return;
      setSocketOverride((prev) => ({
        ...asPlainObject(prev),
        status: incoming.status || prev?.status || 'online',
        deviceStatus: {
          ...asPlainObject(prev?.deviceStatus),
          status: incoming.status || prev?.deviceStatus?.status || 'online',
          lastSeenAt: incoming.timestamp || prev?.deviceStatus?.lastSeenAt || null,
        },
      }));
    };

    socket.on('device_data', mergeTelemetryPayload);
    socket.on('device_status', handleDeviceStatus);
    return () => {
      socket.off('device_data', mergeTelemetryPayload);
      socket.off('device_status', handleDeviceStatus);
    };
  }, [roomId, safeDevice.deviceId, safeDevice.device_id, safeDevice.id]);

  useEffect(() => {
    if (roomId) return undefined;
    const localId = String(safeDevice.deviceId || safeDevice.device_id || safeDevice.id || '');
    if (!localId) return undefined;
    let cancelled = false;
    let inFlight = false;

    const loadLatestWaterTelemetry = async () => {
      if (cancelled || inFlight) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      inFlight = true;
      try {
        const response = await authFetch(
          `/api/sensor-data/water/latest/${encodeURIComponent(localId)}`,
          {},
          { token: localStorage.getItem('token') }
        );
        if (!response.ok || cancelled) return;
        const payload = await response.json();
        if (!payload || cancelled) return;
        const incomingId = getWaterPayloadDeviceId(payload);
        if (incomingId && !particleDeviceIdsEqual(incomingId, localId)) return;
        setSocketOverride((prev) => mergeWaterLivePayload(prev, payload, { source: 'poll' }));
      } catch (_) {
        /* keep the socket/initial data path if polling fails */
      } finally {
        inFlight = false;
      }
    };

    loadLatestWaterTelemetry();
    const intervalId = window.setInterval(loadLatestWaterTelemetry, WATER_LIVE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [roomId, safeDevice.deviceId, safeDevice.device_id, safeDevice.id]);

  const loadDeviceSettings = async ({ refreshControllerConfig = false } = {}) => {
    if (!deviceLookupId) return;
    try {
      const deviceResponse = await authFetch(
        `/api/devices/${deviceLookupId}`,
        {},
        { token: localStorage.getItem('token') }
      );
      if (deviceResponse.ok) {
        const data = await deviceResponse.json();
        setTelegramEnabled(data.telegramEnabled ?? data.alertsEnabled !== false);
        setTrendAlerts(data.trendAlerts ?? data.trendAlertsEnabled ?? false);
        setPowerAlerts(data.powerAlerts ?? data.notifyOnOffline ?? true);
        setThresholdAlerts(data.thresholdAlerts ?? data.alertsEnabled !== false);
      }
      const liveResponse = await authFetch(
        `/api/device/${deviceLookupId}/live-config?refresh=${refreshControllerConfig ? '1' : '0'}`,
        {},
        { token: localStorage.getItem('token') }
      );
      if (!liveResponse.ok) {
        setLiveConfigError('Failed to load live controller config');
        return;
      }
      const live = await liveResponse.json();
      const nextWaterConfigDraft = buildWaterConfigDraft(live.config || {}, safeDevice);
      const nextThresholds = buildWaterThresholdState(live.thresholds || {}, live.config || {});
      setConfig(live.config || {});
      setWaterConfigDraft(nextWaterConfigDraft);
      setInitialWaterConfigDraft(nextWaterConfigDraft);
      setThresholds(nextThresholds);
      setInitialThresholds(nextThresholds);
      setLiveConfigError('');
    } catch (_) {
      setLiveConfigError('Failed to load live controller config');
    }
  };

  useEffect(() => {
    if (safeDevice.customName || safeDevice.name) {
      setName(toDisplay(safeDevice.customName || safeDevice.name));
      return;
    }
    if (!deviceLookupId) {
      setName('');
      return;
    }

    authFetch(`/api/device/${deviceLookupId}/status`, {}, { token: localStorage.getItem('token') })
      .then((r) => r.json())
      .then((data) => data.name && setName(toDisplay(data.name)))
      .catch(() => {});
  }, [deviceLookupId, safeDevice.name, safeDevice.customName]);

  useEffect(() => {
    if (!expanded && !showSettings) return;
    loadDeviceSettings();
  }, [expanded, showSettings]);

  const setThresholdField = (key, part, value) => {
    setThresholds((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [part]: value,
      },
    }));
  };
  const setWaterConfigField = (key, value) => {
    setWaterConfigDraft((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleExpanded = () => setExpanded((prev) => !prev);
  const handleArrowClick = (event) => {
    event.stopPropagation();
    toggleExpanded();
  };

  const saveName = async (event) => {
    event.stopPropagation();
    setNameError('');
    try {
      const res = await authFetch(
        `/api/device/${deviceLookupId}/name`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: toSend(tempName) }),
        },
        { token: localStorage.getItem('token') }
      );
      const data = await res.json();
      if (res.ok && data.name) {
        setName(toDisplay(data.name));
        setEditingName(false);
      } else {
        setNameError(data.error || 'Error');
      }
    } catch (_) {
      setNameError('Error');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {};
      const thresholdsChanged = JSON.stringify(thresholds) !== JSON.stringify(initialThresholds);
      const waterConfigChanged = JSON.stringify(waterConfigDraft) !== JSON.stringify(initialWaterConfigDraft);
      if (thresholdsChanged) {
        EDITABLE_WATER_THRESHOLD_KEYS.forEach((key) => {
          const serialized = serializeWaterThreshold(thresholds[key]);
          if (serialized) payload[key] = serialized;
        });
      }

      const response = await authFetch(
        `/api/device/${deviceLookupId}/settings`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            telegramEnabled,
            powerAlerts,
            thresholdAlerts,
            trendAlerts,
            thresholds: payload,
            config: waterConfigChanged ? serializeWaterConfig(waterConfigDraft) : {},
          }),
        },
        { token: localStorage.getItem('token') }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save settings');
      }
      setMsg({ type: 'success', text: t?.('save') || 'Saved' });
      if (thresholdsChanged || waterConfigChanged) {
        await loadDeviceSettings({ refreshControllerConfig: true });
      }
      setShowSettings(false);
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || 'Failed to save settings' });
    } finally {
      setSavingSettings(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const handleFreshData = async (event) => {
    event.stopPropagation();
    setPendingFreshData(true);
    try {
      const response = await authFetch(
        `/api/device/${deviceLookupId}/callFunction`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ functionName: 'pushNow', arg: '' }),
        },
        { token: localStorage.getItem('token') }
      );
      const data = await response.json();
      if (response.ok && parseInt(data.return_value, 10) === 1) {
        await onRefresh?.(deviceLookupId);
        setMsg({ type: 'success', text: 'OK' });
      } else {
        setMsg({ type: 'error', text: data.error || 'Error' });
      }
    } catch (_) {
      setMsg({ type: 'error', text: 'Error' });
    } finally {
      setPendingFreshData(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const statusItems = [];
  const relayLabels = t?.('relayLabels') || {};
  const statusOnLabel = t?.('statusOn') || 'ON';
  const statusOffLabel = t?.('statusOff') || 'OFF';
  const outletLabel = t?.('water.outlet') || 'OUT';
  const inletLabel = t?.('water.inlet') || 'Inlet';
  const onlineLabel = t?.('online') || 'Online';
  const offlineLabel = t?.('statusOffline') || t?.('offline') || 'Offline';
  const staleDataLabel = t?.('deviceCard.connectivityNoRecentData') || 'No recent data';
  if (safeDevice.rb != null) statusItems.push(`${relayLabels.mask || 'Mask'}: ${safeDevice.rb}`);
  if (config?.mask != null) statusItems.push(`Mask: ${config.mask}`);
  if (config?.status) statusItems.push(`Status: ${config.status}`);
  if (config?.in?.status) statusItems.push(`In: ${config.in.status}`);
  if (config?.out?.status) statusItems.push(`Out: ${config.out.status}`);
  if (inState != null) statusItems.push(`${inletLabel}: ${Number(inState) === 1 ? statusOnLabel : statusOffLabel}`);
  if (outState != null) statusItems.push(`${t?.('water.outletStatus') || 'Outlet'}: ${Number(outState) === 1 ? statusOnLabel : statusOffLabel}`);

  return (
    <>
      <div
        className={`card h-100 text-break w-100 cursor-pointer water-status-card ${statusOnline ? '' : 'water-status-card--offline'} ${roomId ? 'water-status-card--room' : ''}`}
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-12 mb-2">
              <div className="d-flex flex-wrap align-items-center gap-2">
                {editingName ? (
                  <div className="w-100 d-flex flex-wrap align-items-center gap-2">
                    <input
                      type="text"
                      className="form-control form-control-sm d-inline-block"
                      style={{ maxWidth: '220px' }}
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button type="button" className="btn btn-sm btn-success" onClick={saveName}>
                      {t?.('save') || 'Save'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingName(false);
                      }}
                    >
                      {t?.('cancel') || 'Cancel'}
                    </button>
                  </div>
                ) : (
                  <div className="w-100">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-decoration-none water-status-card__title w-100"
                      onClick={handleArrowClick}
                    >
                      {title}
                    </button>
                  </div>
                )}
              </div>
              <div className="text-muted small mt-2">{t?.('updated') || 'Updated'}: {timestampLabel}</div>
              {nameError ? <div className="text-danger small mt-1">{nameError}</div> : null}
            </div>
            <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2 water-status-card__controls">
              <div className="d-flex flex-wrap align-items-center gap-2">
                {!editingName ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTempName(name || title);
                        setEditingName(true);
                      }}
                    >
                      {t?.('edit') || 'Edit'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        loadDeviceSettings({ refreshControllerConfig: true });
                        setShowSettings(true);
                      }}
                    >
                      {t?.('settings') || 'Settings'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleFreshData}
                      disabled={pendingFreshData}
                    >
                      {pendingFreshData ? '...' : t?.('refresh') || 'Refresh'}
                    </button>
                  </>
                ) : null}
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <span className={`badge ${statusOnline ? 'bg-success' : 'bg-secondary'} water-status-card__status`}>
                  {statusOnline ? onlineLabel : offlineLabel}
                </span>
                <span className={`badge ${sensorDataFresh ? 'bg-info-subtle text-info' : 'bg-warning-subtle text-warning'} water-status-card__status`}>
                  {sensorDataFresh ? (t?.('updated') || 'Updated') : staleDataLabel}
                </span>
                {outState != null ? (
                  <span className={`badge ${Number(outState) === 1 ? 'bg-primary-subtle text-primary' : 'bg-secondary-subtle text-secondary'} water-status-card__outlet`}>
                    {outletLabel}: {Number(outState) === 1 ? statusOnLabel : statusOffLabel}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary water-status-card__toggle"
                  onClick={handleArrowClick}
                  aria-label={expanded ? 'Collapse charts' : 'Expand charts'}
                >
                  <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <div className={`water-status-card__accordion ${expanded ? 'is-open' : ''}`}>
            <div className="water-status-card__accordion-inner">
              <div className="row g-2 mx-0">
                <div className="col-12 px-1">
                  {statusItems.length ? (
                    <div className="small text-muted mb-2">{statusItems.join(', ')}</div>
                  ) : null}
                  {msg ? (
                    <div className={`small mb-2 text-${msg.type === 'success' ? 'success' : 'danger'}`}>{msg.text}</div>
                  ) : null}
                  {liveConfigError ? (
                    <div className="small mb-2 text-warning">{liveConfigError}</div>
                  ) : null}
                </div>

                <div className="col-12 px-1">
                  <div className="row g-2 mx-0 align-items-stretch">
                    <div className="col-12 col-lg-4 px-1">
                      <div className="water-status-card__tank-card">
                        <div className="water-status-card__tank-header">
                          <div className="text-muted small text-uppercase">{t?.('tank') || 'Tank'}</div>
                        </div>

                        {hasRealTankData ? (
                          <div className="water-status-card__tank-layout">
                            <div className="water-status-card__tank-volume">
                              {formatCompactNumber(volume, 0)} <span>{t?.('water.units.litersShort') || 'L'}</span>
                            </div>
                            {tankFullVolume != null && Number(tankFullVolume) > 0 ? (
                              <div className="water-status-card__tank-capacity">
                                {t?.('of') || 'of'} {formatCompactNumber(tankFullVolume, 0)} {t?.('water.units.litersShort') || 'L'}
                              </div>
                            ) : null}
                            <div className="water-status-card__tank-shell" aria-hidden="true">
                              <div
                                className="water-status-card__tank-fill"
                                style={{
                                  height: `${fillPercent}%`,
                                  background: `linear-gradient(180deg, rgba(110, 198, 255, 0.95), ${fillColor})`,
                                }}
                              >
                                <div className="water-status-card__tank-level-label">
                                  {formatCompactNumber(level, 0)} {t?.('water.units.centimetersShort') || t?.('centimetersShort') || 'cm'}
                                </div>
                              </div>
                            </div>
                            {tankMaxHeight != null ? (
                              <div className="water-status-card__tank-meta text-muted small">
                                {t?.('maxHeight') || 'Max height'}: {formatCompactNumber(tankMaxHeight, 0)} {t?.('water.units.centimetersShort') || t?.('centimetersShort') || 'cm'}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="water-status-card__tank-placeholder" title={t?.('noTankData') || 'No tank data'}>
                            <span>{t?.('noData') || 'No data'}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-lg-8 px-1">
                      <div className="row g-2 mx-0 h-100">
                    <div className="col-6 col-lg-6 px-1">
                      <MetricTile
                        icon="fas fa-flask"
                        label="pH"
                        value={formatNumber(ph, 2)}
                        tone={phError ? 'text-danger water-status-card__metric-value--pulse' : phTone}
                        prominent
                        hasError={phError}
                        hint={
                          ph == null
                            ? ''
                            : phError
                            ? t?.('water.errors.ph_out_of_range') || 'pH out of range'
                            : t?.('water.hints.ph_optimal') || 'Optimal range'
                        }
                      />
                    </div>
                    <div className="col-6 col-lg-6 px-1">
                      <MetricTile
                        icon="fas fa-bolt"
                        label="EC"
                        value={formatNumber(ec, 3)}
                        unit="mS/cm"
                        prominent
                        hasError={ecError}
                        tone={ecError ? 'text-danger water-status-card__metric-value--pulse' : ''}
                      />
                    </div>
                    <div className="col-6 col-lg-6 px-1">
                      <MetricTile
                        icon="fas fa-wave-square"
                        label="ORP"
                        value={formatNumber(orp, 0)}
                        unit="mV"
                        hasError={orpError}
                        tone={orpError ? 'text-danger water-status-card__metric-value--pulse' : 'text-muted'}
                      />
                    </div>
                    <div className="col-6 col-lg-6 px-1">
                      <MetricTile
                        icon="fas fa-thermometer-half"
                        label={t?.('temperature') || 'Temp'}
                        value={formatNumber(ecTemp, 1)}
                        unit="°C"
                        tone={tempWarning ? 'text-danger' : 'text-muted'}
                        hint={tempWarning ? t?.('water.errors.temp_high') || 'Warning: above 28°C' : ''}
                      />
                    </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0">
                      <tbody>
                        <tr>
                          <th>pH</th>
                          <td>{formatNumber(ph, 2)}</td>
                        </tr>
                        <tr>
                          <th>EC</th>
                          <td>{formatNumber(ec, 3)}</td>
                        </tr>
                        <tr>
                          <th>{t?.('temperature') || 'Temp'}</th>
                          <td>{formatNumber(ecTemp, 1)}</td>
                        </tr>
                        <tr>
                          <th>{t?.('waterVolume') || 'Volume'}</th>
                          <td>{formatCompactNumber(volume, 0)}</td>
                        </tr>
                        <tr>
                          <th>{t?.('waterLevel') || 'Water level'}</th>
                          <td>{formatCompactNumber(levelCm, 1)} cm</td>
                        </tr>
                        <tr>
                          <th>{t?.('levelVoltage') || 'Level voltage'}</th>
                          <td>{formatNumber(levelVoltage, 3)} V</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="mt-2">
                    <WaterCharts deviceId={subtitle} t={t} />
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="water-status-card__footer">
                    <span className="text-muted small">{subtitle}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeviceSettingsModal
        show={showSettings}
        onClose={() => setShowSettings(false)}
        onSave={saveSettings}
        saving={savingSettings}
        controller="water"
        t={t}
        telegramEnabled={telegramEnabled}
        setTelegramEnabled={setTelegramEnabled}
        powerAlerts={powerAlerts}
        setPowerAlerts={setPowerAlerts}
        thresholdAlerts={thresholdAlerts}
        setThresholdAlerts={setThresholdAlerts}
        trendAlerts={trendAlerts}
        setTrendAlerts={setTrendAlerts}
        thresholds={thresholds}
        setThresholdField={setThresholdField}
        waterConfig={waterConfigDraft}
        setWaterConfigField={setWaterConfigField}
      />
    </>
  );
}
